import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { Table } from '@/components';
import { vendorCourierApi } from '@/services/vendorCourierApi';
import { CourierListItem } from '@/types/courier';

type StatusFilter = 'all' | 'active' | 'inactive';

const STATUS_LABELS: Record<string, string> = {
  active: 'Aktif',
  inactive: 'İnaktif',
};

const formatDate = (value: unknown) => {
  const parsed = value ? new Date(value as string | number | Date) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed.toLocaleDateString('tr-TR') : '-';
};

const VendorCouriersPage: React.FC = () => {
  const navigate = useNavigate();
  const [couriers, setCouriers] = useState<CourierListItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [loading, setLoading] = useState(true);

  const loadCouriers = async () => {
    setLoading(true);
    try {
      const response = await vendorCourierApi.getCouriers({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        limit: 100,
        offset: 0,
      });
      setCouriers(response.couriers || []);
    } catch (error: any) {
      console.error('Vendor kuryeleri yüklenemedi:', error);
      toast.error(error.message || 'Kuryeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCouriers();
  }, [statusFilter]);

  const tableData = couriers.map((courier) => ({
    id: courier.id,
    name: `${courier.firstName} ${courier.lastName}`,
    email: courier.email,
    phone: courier.phone,
    status: STATUS_LABELS[courier.status] || courier.status,
    deliveries: courier.totalDeliveries || 0,
    date: formatDate(courier.createdAt),
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-dark dark:text-light">Kuryelerim</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Sadece size bağlı kuryeler burada listelenir</p>
        </div>
        <button onClick={() => navigate('/vendor/couriers/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
          <Plus size={18} />
          Kurye Ekle
        </button>
      </div>

      <div className="card w-full md:max-w-xs">
        <label className="block text-sm font-medium mb-2">Durum</label>
        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusFilter)} className="input-field">
          <option value="all">Hepsi</option>
          <option value="active">Aktif</option>
          <option value="inactive">İnaktif</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-12"><div className="animate-spin text-4xl">⏳</div></div>
      ) : (
        <Table
          headers={['Ad', 'E-posta', 'Telefon', 'Teslim', 'Durum', 'Oluşturma Tarihi']}
          data={tableData}
          renderCell={(item, header) => {
            const headerMap: Record<string, string> = {
              'Ad': 'name',
              'E-posta': 'email',
              'Telefon': 'phone',
              'Teslim': 'deliveries',
              'Durum': 'status',
              'Oluşturma Tarihi': 'date',
            };

            const key = headerMap[header];
            return key ? item[key as keyof typeof item] : '-';
          }}
          actions={(item) => (
            <button
              onClick={() => navigate(`/vendor/couriers/${item.id}`)}
              className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 rounded transition-smooth"
              title="Düzenle"
            >
              <Edit2 size={16} />
            </button>
          )}
        />
      )}
    </div>
  );
};

export default VendorCouriersPage;